import { default as React, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Alert, AlertPriority, AlertToken, Reminder } from "../../redux/alerts";
import {
  alertsAfterGateById,
  remindersAfterGateById,
  residentCardColor,
  sortAlertsAndRemindersForCard,
} from "../../redux/alerts/selectors";
import { DeviceType } from "../../redux/devices";
import { doesAnyDeviceHasProblemCreator } from "../../redux/devicesStatuses/selectors";
import {
  residentDevicesAfterGateCreator,
  residentsCreator,
} from "../../redux/residents/selectors";
import { IAppState } from "../../redux/types";
import { alertIconToken } from "../../utils/AlertTo";
import { AvatarResidentUser } from "../common/AvatarUser";
import { OverflowTip, RoomBedDisplayByMin } from "../common/Common";
import { ModalTypes } from "../status/Callbacks";
import { TimePass } from "../status/OneNotificationView";
import {
  MarkForBed,
  MarkForNearRoom,
  MarkForResidentAvatar,
} from "../status/utils";
const FullResidentCard = React.memo(
  ({
    residentId,
    callback,
  }: {
    residentId: string;
    callback: (...args: any) => void;
  }): JSX.Element => {
    const ResidentSelection = useMemo(() => residentsCreator(), []);
    const resident = useSelector((state: IAppState) =>
      ResidentSelection(state, residentId)
    );
    const selection2 = useMemo(() => residentCardColor(), []);
    const colorPriority = useSelector((state: IAppState) =>
      selection2(state, residentId)
    );
    const memoCallbak = useMemo(() => callback, [callback]);
    const cardClassName = `card ${
      colorPriority === AlertPriority.high
        ? "red"
        : colorPriority === AlertPriority.low
        ? "yellow"
        : colorPriority === AlertPriority.med
        ? "black"
        : ""
    }`;
    const devicesSelection = useMemo(
      () => residentDevicesAfterGateCreator(),
      []
    );
    const residentDevices = useSelector((state: IAppState) =>
      devicesSelection(state, residentId)
    );
    const hasOnlyWatch =
      Object.keys(residentDevices).find(
        (el) => residentDevices[el].type === DeviceType.watch
      ) && Object.keys(residentDevices).length === 1;
    const devisesAbsent = resident.devices.length === 0;
    const selection = useMemo(() => doesAnyDeviceHasProblemCreator(), []);
    const hasProblems = useSelector((state: IAppState) =>
      selection(state, residentDevices)
    );
    const onClickDeviceStatuses = useCallback(
      (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        memoCallbak(ModalTypes.SHOW_DEVICES_STATUSES, residentId);
      },
      [residentId, memoCallbak]
    );
    const onShowResidentStatus = useCallback(() => {
      memoCallbak(ModalTypes.SHOW_RESIDENT_STATUSES, residentId);
    }, [residentId, memoCallbak]);
    const onShowUserNearRoomStatus = useCallback(() => {
      memoCallbak(ModalTypes.SHOW_USER_NEAR_ROOM_STATUSES, residentId);
    }, [residentId, memoCallbak]);

    const memoizedProfile = useMemo(() => resident, [resident]);
    return (
      <div className="full-card-holder">
        <div
          className={`one-notif-${cardClassName} card-full-view flex-column`}
        >
          <RoomBedDisplayByMin devicesMinimized={resident.devicesMinimized} />

          <div className="flex-row flex-row-8">
            <React.Suspense fallback={<p></p>}>
              {!hasOnlyWatch ? (
                <div className="flex-column">
                  {MarkForNearRoom({ residentId: residentId }) && (
                    <span
                      onClick={onShowUserNearRoomStatus}
                      className="bed-holder"
                    >
                      <MarkForNearRoom residentId={residentId} />
                    </span>
                  )}
                  <span onClick={onShowResidentStatus}>
                    <MarkForBed residentId={residentId} />
                  </span>
                </div>
              ) : (
                <i className={`icon-info icon-big icon-device-watch `} />
              )}

              <AvatarResidentUser
                size="68"
                profile={memoizedProfile}
                view={"card"}
              >
                <MarkForResidentAvatar resident={memoizedProfile} />
              </AvatarResidentUser>
            </React.Suspense>
            {!devisesAbsent && (
              <i
                className={`icon-info icon-big ${hasProblems ? "red" : ""}`}
                onClick={onClickDeviceStatuses}
              />
            )}
          </div>
          <OverflowTip
            text={[resident.firstName, resident.lastName].join(" ")}
            className="center"
          />
          <React.Suspense fallback={<p></p>}>
            <AlertsList callback={memoCallbak} residentId={residentId} />
          </React.Suspense>
        </div>
      </div>
    );
  }
);

export const AlertsList = React.memo(
  ({
    residentId,
    callback,
    size = "32",
  }: {
    residentId: string;
    callback: (...args: any) => void;
    size?: string;
  }) => {
    const alerts = useSelector((state: IAppState) =>
      alertsAfterGateById(state, residentId)
    );
    const reminders = useSelector((state: IAppState) =>
      remindersAfterGateById(state, residentId)
    );
    const handleOneAlertClick = useCallback(
      (el: Alert) => {
        if (callback) callback(ModalTypes.SHOW_ALERT_FEEDBACK, el);
      },
      [callback]
    );
    const handleOneActivityClick = useCallback(
      (el: Reminder) => {
        if (callback)
          callback(ModalTypes.SHOW_ACTIVITY_FEEDBACK, el, residentId);
      },
      [callback, residentId]
    );
    const together = [...alerts, ...(reminders || [])];
    const togetherSorted = together.sort(sortAlertsAndRemindersForCard);
    useEffect(() => {
      if (alerts.length === 0 && reminders.length === 0)
        callback(ModalTypes.BACK);
    }, [alerts, reminders, callback]);
    return togetherSorted.length > 0 ? (
      <div className="flex-column alerts">
        {togetherSorted.map((el, index) => {
          const isReminder = "activityType" in el;
          return (
            <div className={`alert-${size}`} key={index}>
              {!isReminder ? (
                <OneAlert
                  el={el as Alert}
                  handleOneAlertClick={handleOneAlertClick}
                />
              ) : (
                <OneActivity
                  el={el as Reminder}
                  handleOneActivityClick={handleOneActivityClick}
                />
              )}
            </div>
          );
        })}
        {/* {togetherSorted.length > 5 && (
          <div className="badge">{togetherSorted.length - 5}</div>
        )} */}
      </div>
    ) : null;
  }
);
AlertsList.displayName = "AlertsList";
const OneAlert = React.memo(
  ({
    el,
    handleOneAlertClick,
  }: {
    el: Alert;
    handleOneAlertClick: (a: Alert) => void;
  }) => {
    const { t } = useTranslation("common");
    const memoAl = useMemo(() => el, [el]);

    return (
      <div
        className="one-open-notif flex-column"
        style={{ marginBottom: "32px" }}
      >
        <div className="flex-column flex-center">
          <p>{t("alerts." + el?.alertToken)}</p>
          <div style={{ marginTop: "-7px" }}>
            <TimePass alert={memoAl} />
          </div>

          <div className={`alert-32`}>
            {alertIconToken(el["alertToken"] as AlertToken)}
          </div>
        </div>
        {el?.alertToken !== AlertToken.UserNearby && (
          <button
            className="btn primary  with-icon save-view"
            style={{ marginTop: "10px" }}
            onClick={() => handleOneAlertClick(el)}
          >
            <i className="icon-feedback-done" />
            {t("buttons.Done")}
          </button>
        )}
      </div>
    );
  }
);
OneAlert.displayName = "OneAlert";
const OneActivity = React.memo(
  ({
    el,
    handleOneActivityClick,
  }: {
    el: Reminder;
    handleOneActivityClick: (a: Reminder) => void;
  }) => {
    const { t } = useTranslation("common");
    const memoAl = useMemo(() => el, [el]);
    return (
      <div
        className="one-open-notif flex-column"
        style={{ marginBottom: "32px" }}
      >
        <div className="flex-column flex-center">
          <p>{t("activities." + el["activityType"])}</p>
          <div style={{ marginTop: "-7px" }}>
            <TimePass alert={memoAl} />
          </div>
          <div className={`alert-32`}>
            <i
              className={`icon-alert info icon-action-${el["activityType"]}`}
            />
          </div>
        </div>
        <button
          className="btn primary  with-icon save-view"
          onClick={() => handleOneActivityClick(el)}
          style={{ marginTop: "10px" }}
        >
          <i className="icon-feedback-done" />
          {t("buttons.Done")}
        </button>
      </div>
    );
  }
);
OneActivity.displayName = "OneActivity";
FullResidentCard.displayName = "FullResidentCard";
export default React.memo(FullResidentCard);
